import { useMemo, useState } from 'react';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { useAuth } from '../../../../shared/useAuth.js';
import { useRules, useCreateRule, useUpdateRule, useDeleteRule, useSetDefaultRule } from '../../hooks/useRules.js';
import { RuleRow } from './RuleRow.jsx';
import { RuleForm } from './RuleForm.jsx';
import { ConfirmModal } from '../ConfirmModal.jsx';

export function RulesList() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canEdit = hasPermission('pricing.edit');
  const { data: rules = [], isLoading, error } = useRules();
  const createRule = useCreateRule();
  const updateRule = useUpdateRule();
  const deleteRule = useDeleteRule();
  const setDefault = useSetDefaultRule();

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDefault, setConfirmDefault] = useState(null);

  const currentDefault = useMemo(() => rules.find((r) => r.isDefault) || null, [rules]);

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(rule) {
    setEditing(rule);
    setFormOpen(true);
  }
  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  async function handleSubmit(body) {
    if (editing) {
      await updateRule.mutateAsync(
        { id: editing._id || editing.id, body },
        { onSuccess: () => closeForm() }
      );
    } else {
      await createRule.mutateAsync(body, { onSuccess: () => closeForm() });
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[15px] font-bold">قواعد التسعير</div>
          <div className="text-xs text-ink-3 mt-0.5">قواعد الاحتساب الساعي ومعاييرها</div>
        </div>
        {canEdit ? (
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" size={14} /> قاعدة جديدة
          </button>
        ) : null}
      </div>

      {error ? (
        <Card>
          <div className="text-red text-center py-4">تعذّر تحميل قواعد التسعير</div>
        </Card>
      ) : isLoading ? (
        <Card>
          <div className="text-ink-3 text-center py-8">جاري التحميل...</div>
        </Card>
      ) : rules.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10 flex flex-col items-center gap-3">
            <Icon name="pricing" size={42} color="#D6CCB3" />
            <div className="text-[14px] font-semibold text-ink-2">
              لا توجد قواعد بعد — أنشئ قاعدتك الأولى
            </div>
            {canEdit ? (
              <button type="button" className="btn btn-primary" onClick={openCreate}>
                <Icon name="plus" size={14} /> قاعدة جديدة
              </button>
            ) : null}
          </div>
        </Card>
      ) : (
        <Card>
          <table className="w-full text-[13px]">
            <thead className="text-ink-2 text-[12px]">
              <tr className="border-b border-line">
                <th className="p-2.5 text-start">الاسم</th>
                <th className="p-2.5 text-start">سعر الساعة</th>
                <th className="p-2.5 text-start">أقل مبلغ</th>
                <th className="p-2.5 text-start">السماح</th>
                <th className="p-2.5 text-start">التقريب</th>
                <th className="p-2.5 text-start">سعر العطلة</th>
                <th className="p-2.5 text-end">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <RuleRow
                  key={r._id || r.id}
                  rule={r}
                  canEdit={canEdit}
                  onEdit={openEdit}
                  onDelete={(rule) => setConfirmDelete(rule)}
                  onSetDefault={(rule) => setConfirmDefault(rule)}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <RuleForm
        open={formOpen}
        rule={editing}
        currentDefault={currentDefault}
        onClose={closeForm}
        onSubmit={handleSubmit}
        submitting={createRule.isPending || updateRule.isPending}
      />

      <ConfirmModal
        open={!!confirmDelete}
        title="حذف قاعدة التسعير"
        message={`هل أنت متأكد من حذف قاعدة "${confirmDelete?.name || ''}"؟ لا يمكن التراجع عن هذه العملية.`}
        confirmLabel="حذف"
        variant="danger"
        loading={deleteRule.isPending}
        onClose={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deleteRule.mutateAsync(
            confirmDelete._id || confirmDelete.id,
            { onSuccess: () => setConfirmDelete(null) }
          );
        }}
      />

      <ConfirmModal
        open={!!confirmDefault}
        title="تعيين كافتراضي"
        message={`ستصبح القاعدة "${confirmDefault?.name || ''}" الافتراضية، وسيتم إلغاء افتراضية القاعدة الحالية${currentDefault ? ` "${currentDefault.name}"` : ''}.`}
        confirmLabel="تعيين"
        loading={setDefault.isPending}
        onClose={() => setConfirmDefault(null)}
        onConfirm={async () => {
          if (!confirmDefault) return;
          await setDefault.mutateAsync(
            confirmDefault._id || confirmDefault.id,
            { onSuccess: () => setConfirmDefault(null) }
          );
        }}
      />
    </div>
  );
}
